import {
  CreatorFilter,
  DateRangeFilter,
  MentionTypeFilter,
  PlatformFilter,
} from '@/components/filters';
import { FilterGroup } from '@/components/filters/filter-button';
import { FilterPanel } from '@/components/ui';
import { Button, Flex, HStack } from '@chakra-ui/react';
import { Xmark } from 'iconoir-react';

import { parseAsString, useQueryState } from 'nuqs';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface MentionsFiltersProps {
  paramPrefix?: string;
}

const MentionsFilters: React.FC<MentionsFiltersProps> = ({ paramPrefix = '' }) => {
  const { t } = useTranslation();

  const platformParam = paramPrefix ? `${paramPrefix}MentionPlatform` : 'mentionPlatform';
  const typeParam = paramPrefix ? `${paramPrefix}MentionType` : 'mentionType';
  const creatorIdsParam = paramPrefix ? `${paramPrefix}CreatorIds` : 'creatorIds';
  const dateRangePrefix = paramPrefix ? `${paramPrefix}mention` : 'mention';
  const startDateParam = `${dateRangePrefix}StartDate`;
  const endDateParam = `${dateRangePrefix}EndDate`;

  const [platform, setPlatform] = useQueryState(platformParam, parseAsString);
  const [mentionType, setMentionType] = useQueryState(typeParam, parseAsString);
  const [creatorIds, setCreatorIds] = useQueryState(creatorIdsParam, parseAsString);
  const [startDate, setStartDate] = useQueryState(startDateParam, parseAsString);
  const [endDate, setEndDate] = useQueryState(endDateParam, parseAsString);

  const handleMentionTypeChange = useCallback(
    (newType: string) => {
      setMentionType(newType || null);
    },
    [setMentionType]
  );

  const handleResetFilters = useCallback(() => {
    setPlatform(null);
    setMentionType(null);
    setCreatorIds(null);
    setStartDate(null);
    setEndDate(null);
  }, [setPlatform, setMentionType, setCreatorIds, setStartDate, setEndDate]);

  const hasActiveFilters = Boolean(platform || mentionType || creatorIds || startDate || endDate);

  return (
    <FilterPanel>
      <FilterGroup>
        <Flex justify="space-between" align="center" gap={2} flexWrap="wrap" width="100%">
          <HStack gap={2} flexWrap="wrap">
            <PlatformFilter value={platform} onChange={setPlatform} />
            <MentionTypeFilter value={mentionType || ''} onChange={handleMentionTypeChange} />
            <CreatorFilter paramPrefix={paramPrefix} />
            <DateRangeFilter paramPrefix={dateRangePrefix} />
          </HStack>

          {hasActiveFilters && (
            <Button variant="ghost" colorPalette="gray" size="sm" onClick={handleResetFilters}>
              <Xmark width={14} height={14} />
              {t('mentions.filters.reset')}
            </Button>
          )}
        </Flex>
      </FilterGroup>
    </FilterPanel>
  );
};

export default MentionsFilters;
